// frontend/src/pages/Pricing/Pricing.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Card, CardContent,
  ToggleButton, ToggleButtonGroup, Chip, Divider, Alert,
  CircularProgress, useTheme, useMediaQuery
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// Feature lists for each tier
const TIER_FEATURES = {
  free: [
    'Unlimited questions & worked examples (all grades)',
    'Full social features (posts, comments, likes)',
    'ForYou, Following & Metrics feeds',
    'Basic progress tracking',
    '2 hints per question',
    'Save up to 20 items'
  ],
  pro: [
    'Everything in Free, plus:',
    'Smart Study (spaced repetition)',
    'Personalised weekly study plans',
    'Detailed analytics dashboard',
    'Exam countdown revision schedule',
    'Unlimited hints & bookmarks',
    'Performance benchmarking',
    'Mistake pattern analysis',
    'Custom study sessions',
    'Downloadable certificates',
    'Ad-free experience'
  ],
  family: [
    'Everything in Pro for up to 3 learners:',
    'Parent Dashboard',
    'Weekly progress reports per child',
    'Study time insights',
    'Collaborative goal setting',
    'Sibling comparison (toggleable)',
    'Priority support'
  ],
  school: [
    'Everything in Pro for all learners:',
    'Teacher Dashboard & class management',
    'Class analytics & topic mastery rates',
    'CAPS-aligned curriculum reports',
    'Bulk account creation (CSV upload)',
    'Quarterly impact reports',
    'Dedicated support',
    'Priority content error reporting'
  ]
};

const TIER_ICONS = {
  free: <SchoolIcon sx={{ fontSize: 32 }} />,
  pro: <RocketLaunchIcon sx={{ fontSize: 32 }} />,
  family: <GroupsIcon sx={{ fontSize: 32 }} />,
  school: <StarIcon sx={{ fontSize: 32 }} />
};

const TIER_COLORS = {
  free: '#6B7280',
  pro: '#2563EB',
  family: '#7C3AED',
  school: '#059669'
};

const Pricing = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  const [billing, setBilling] = useState('monthly');
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [currentTier, setCurrentTier] = useState('free');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch pricing and current subscription status
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricingRes, statusRes] = await Promise.all([
          api.get('/api/subscription/pricing'),
          user ? api.get('/api/subscription/status').catch(() => null) : null
        ]);

        if (pricingRes.data.success) {
          setPricing(pricingRes.data.data);
        }

        if (statusRes?.data?.success) {
          setCurrentTier(statusRes.data.data.tier);
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const getPrice = (tier) => {
    if (!pricing || !pricing[tier]) return null;
    return billing === 'monthly' ? pricing[tier].monthly : pricing[tier].annual;
  };

  const getSavings = (tier) => {
    if (!pricing || !pricing[tier]) return 0;
    const monthly = pricing[tier].monthly;
    const annual = pricing[tier].annual;
    if (!monthly || !annual) return 0;
    return Math.round(((monthly * 12 - annual) / (monthly * 12)) * 100);
  };

  const handleStartTrial = async (tier) => {
    if (!user) {
      navigate('/auth/register');
      return;
    }
    setActionLoading(tier);
    setError(null);
    try {
      const res = await api.post('/api/subscription/start-trial', { tier });
      if (res.data.success) {
        setSuccess(res.data.data.message);
        setCurrentTier(tier);
        setTimeout(() => navigate('/home'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start trial');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCheckout = async (tier) => {
    if (!user) {
      navigate('/auth/register');
      return;
    }
    setActionLoading(tier);
    setError(null);
    try {
      const res = await api.post('/api/subscription/checkout', {
        tier,
        billingCycle: billing
      });
      if (res.data.success) {
        // Redirect to Paystack checkout
        window.location.href = res.data.data.checkoutUrl;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start checkout');
    } finally {
      setActionLoading(null);
    }
  };

  const handleContactSales = () => {
    window.location.href = 'mailto:hello@pedaplate.co.za?subject=MathZA for Schools Enquiry';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Choose Your Plan
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
            Free learning forever. Upgrade for smarter study tools that help you ace your exams.
          </Typography>

          {/* Billing Toggle */}
          <ToggleButtonGroup
            value={billing}
            exclusive
            onChange={(e, val) => val && setBilling(val)}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              p: 0.5
            }}
          >
            <ToggleButton value="monthly" sx={{ px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Monthly
            </ToggleButton>
            <ToggleButton value="annual" sx={{ px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Annual
              <Chip label="Save up to 30%" size="small" color="success" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Pricing Cards */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
          gap: 3,
          alignItems: 'stretch'
        }}>
          {/* FREE */}
          <PricingCard
            tier="free"
            name="Free"
            price={0}
            period=""
            description="Learn maths, your way"
            features={TIER_FEATURES.free}
            color={TIER_COLORS.free}
            icon={TIER_ICONS.free}
            isCurrentTier={currentTier === 'free'}
            ctaText={currentTier === 'free' ? 'Current Plan' : 'Downgrade'}
            ctaDisabled={currentTier === 'free'}
            onCta={() => {}}
            loading={false}
          />

          {/* PRO — highlighted */}
          <PricingCard
            tier="pro"
            name="Pro"
            price={getPrice('pro')}
            period={billing === 'monthly' ? '/month' : '/year'}
            description="Study smarter, not harder"
            features={TIER_FEATURES.pro}
            color={TIER_COLORS.pro}
            icon={TIER_ICONS.pro}
            highlighted
            savings={billing === 'annual' ? getSavings('pro') : 0}
            isCurrentTier={currentTier === 'pro'}
            trialAvailable={currentTier === 'free'}
            ctaText={currentTier === 'pro' ? 'Current Plan' : 'Start 7-Day Free Trial'}
            ctaDisabled={currentTier === 'pro'}
            onCta={() => handleStartTrial('pro')}
            onCheckout={() => handleCheckout('pro')}
            loading={actionLoading === 'pro'}
          />

          {/* FAMILY */}
          <PricingCard
            tier="family"
            name="Family"
            price={getPrice('family')}
            period={billing === 'monthly' ? '/month' : '/year'}
            description="For up to 3 learners"
            features={TIER_FEATURES.family}
            color={TIER_COLORS.family}
            icon={TIER_ICONS.family}
            savings={billing === 'annual' ? getSavings('family') : 0}
            isCurrentTier={currentTier === 'family'}
            trialAvailable={currentTier === 'free'}
            ctaText={currentTier === 'family' ? 'Current Plan' : 'Start 7-Day Free Trial'}
            ctaDisabled={currentTier === 'family'}
            onCta={() => handleStartTrial('family')}
            onCheckout={() => handleCheckout('family')}
            loading={actionLoading === 'family'}
          />

          {/* SCHOOL */}
          <PricingCard
            tier="school"
            name="Schools"
            price={null}
            period=""
            description="For your entire school"
            features={TIER_FEATURES.school}
            color={TIER_COLORS.school}
            icon={TIER_ICONS.school}
            isCurrentTier={currentTier === 'school'}
            ctaText="Contact Sales"
            ctaDisabled={false}
            onCta={handleContactSales}
            loading={false}
            isSchool
            schoolPricing={pricing?.school?.tiers}
          />
        </Box>

        {/* FAQ / Bottom Note */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body2" color="text.secondary">
            All plans include unlimited questions and worked examples across all grades and topics.
            <br />
            We never limit your ability to practise maths — only the optimisation tools are premium.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

// ============================================================
// PricingCard Component
// ============================================================
const PricingCard = ({
  tier, name, price, period, description, features, color, icon,
  highlighted, savings, isCurrentTier, trialAvailable, ctaText,
  ctaDisabled, onCta, onCheckout, loading, isSchool, schoolPricing
}) => {
  return (
    <Card
      elevation={highlighted ? 8 : 1}
      sx={{
        position: 'relative',
        border: highlighted ? `2px solid ${color}` : '1px solid',
        borderColor: highlighted ? color : 'divider',
        borderRadius: 3,
        overflow: 'visible',
        transform: highlighted ? { md: 'scale(1.05)' } : 'none',
        zIndex: highlighted ? 1 : 0,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 6 }
      }}
    >
      {/* Popular badge */}
      {highlighted && (
        <Chip
          label="Most Popular"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: color,
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.75rem'
          }}
        />
      )}

      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Icon + Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="h6" fontWeight={700}>{name}</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {/* Price */}
        {isSchool ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              From
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              R5,000
              <Typography component="span" variant="body2" color="text.secondary">/year</Typography>
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            {price !== null && price !== undefined ? (
              <>
                <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1 }}>
                  R{price?.toLocaleString()}
                  <Typography component="span" variant="body1" color="text.secondary" fontWeight={400}>
                    {period}
                  </Typography>
                </Typography>
                {savings > 0 && (
                  <Chip
                    label={`Save ${savings}%`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </>
            ) : (
              <Typography variant="h3" fontWeight={700}>Free</Typography>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Features */}
        <Box sx={{ flexGrow: 1, mb: 2 }}>
          {features.map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.8 }}>
              <CheckCircleIcon sx={{ fontSize: 18, color, mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                {f}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* School pricing tiers */}
        {isSchool && schoolPricing && (
          <Box sx={{ mb: 2 }}>
            {schoolPricing.map((t, i) => (
              <Typography key={i} variant="caption" display="block" color="text.secondary">
                {t.label}: R{t.annual?.toLocaleString()}/year
              </Typography>
            ))}
          </Box>
        )}

        {/* CTA Buttons */}
        <Box>
          {isCurrentTier ? (
            <Button
              fullWidth
              variant="outlined"
              disabled
              sx={{ borderRadius: 2, textTransform: 'none', py: 1.2, fontWeight: 600 }}
            >
              Current Plan
            </Button>
          ) : (
            <>
              <Button
                fullWidth
                variant={highlighted ? 'contained' : 'outlined'}
                onClick={onCta}
                disabled={ctaDisabled || loading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  py: 1.2,
                  fontWeight: 600,
                  bgcolor: highlighted ? color : 'transparent',
                  borderColor: color,
                  color: highlighted ? '#fff' : color,
                  '&:hover': {
                    bgcolor: highlighted ? color : `${color}10`,
                    borderColor: color
                  }
                }}
              >
                {loading ? <CircularProgress size={22} /> : ctaText}
              </Button>

              {/* "Skip trial, subscribe now" link */}
              {trialAvailable && onCheckout && (
                <Button
                  fullWidth
                  size="small"
                  onClick={onCheckout}
                  sx={{
                    mt: 0.5,
                    textTransform: 'none',
                    color: 'text.secondary',
                    fontSize: '0.75rem'
                  }}
                >
                  Skip trial — subscribe now
                </Button>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Pricing;
